import { WithDisposable } from '@blocksuite/block-std';
import {
  getCategoryColors,
  getCategoryDisplayName,
  type NodeTagType,
} from '@pique/core/components/blocksuite/block-suite-editor/specs/custom/pique/overlays/node-categories-config';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { styleMap } from 'lit/directives/style-map.js';

import {
  MindmapElementModel,
  type MindmapNode,
} from '../../../../surface-block/element-model/mindmap.js';
import { getDepthScale } from '../../../../surface-block/element-model/utils/mindmap/style.js';
import type { EdgelessRootBlockComponent } from '../../edgeless-root-block.js';

type CategoryTagInfo = {
  id: string;
  nodeId: string;
  mindmap: MindmapElementModel;
  node: MindmapNode;
  label: string;
  colors: { bg: string; border: string; text: string };
  x: number;
  y: number;
  width: number;
  height: number;
  depth: number;
};

const TAG_SIZE = 16;
void TAG_SIZE;

@customElement('edgeless-mindmap-node-categories')
export class EdgelessMindmapNodeCategories extends WithDisposable(LitElement) {
  static override styles = css`
    .mindmap-categories-container {
      position: absolute;
      top: 0;
      left: 0;
      pointer-events: none;
      z-index: 1;
    }

    .category-tag-wrapper {
      width: 16px;
      height: 16px;
      position: absolute;
      z-index: 1;
      pointer-events: auto;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .category-tag {
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 13px;
      font-weight: 450;
      font-family: var(--affine-font-family);
      white-space: nowrap;
    }
  `;

  @property({ attribute: false })
  accessor edgeless!: EdgelessRootBlockComponent;

  @state()
  private accessor _tags: CategoryTagInfo[] = [];

  override connectedCallback() {
    super.connectedCallback();

    this._updateTags();

    this._disposables.add(
      this.edgeless.service.viewport.viewportUpdated.on(() => {
        this.requestUpdate();
      })
    );

    this._disposables.add(
      this.edgeless.surfaceBlockModel.elementUpdated.on(() => {
        this._updateTags();
      })
    );

    this._disposables.add(
      this.edgeless.surfaceBlockModel.elementAdded.on(() => {
        this._updateTags();
      })
    );

    this._disposables.add(
      this.edgeless.surfaceBlockModel.elementRemoved.on(() => {
        this._updateTags();
      })
    );
  }

  private _updateTags() {
    const tags: CategoryTagInfo[] = [];
    const elements =
      (this.edgeless.surfaceBlockModel as unknown as { getElementsByType(type: string): unknown[] }).getElementsByType('mindmap');

    for (const element of elements) {
      if (!(element instanceof MindmapElementModel)) continue;

      const mindmap = element;
      const getMeta =
        typeof (mindmap as any).getNodeMetadata === 'function'
          ? (id: string) => (mindmap as any).getNodeMetadata(id)
          : null;

      mindmap.traverse(
        (node: MindmapNode) => {
          const detail = node.detail as { category?: string | null };
          const meta = getMeta ? getMeta(node.id) : detail;
          const category = meta?.category ?? null;

          if (!category) return;

          const bound = node.element.elementBound;
          let label: string;
          let colors: { bg: string; border: string; text: string };

          try {
            label = getCategoryDisplayName(category as NodeTagType);
            colors = getCategoryColors(category as NodeTagType);
          } catch {
            label = String(category);
            colors = { bg: '#f0f0f0', border: '#ccc', text: '#333' };
          }

          const path = mindmap.getPath(node.id);
          const depth = path.length - 1;

          tags.push({
            id: `${mindmap.id}-${node.id}`,
            nodeId: node.id,
            mindmap,
            node,
            label,
            colors,
            x: bound.x,
            y: bound.y,
            width: bound.w,
            height: bound.h,
            depth,
          });
        },
        { stopOnCollapse: true }
      );
    }

    this._tags = tags;
  }

  override render() {
    if (this._tags.length === 0) {
      return nothing;
    }

    const { viewport } = this.edgeless.service;
    const { zoom } = viewport;

    return html`
      <div class="mindmap-categories-container">
        ${repeat(
          this._tags,
          info => info.id,
          info => {
            const [nodeViewX, nodeViewY] = viewport.toViewCoord(info.x, info.y);
            const nodeViewWidth = info.width * zoom;
            const nodeViewHeight = info.height * zoom;

            const depthScale = getDepthScale(info.depth);
            const fontSize = Math.round(11 * depthScale);
            const padding = Math.round(4 * depthScale);

            const tagX = nodeViewX + nodeViewWidth * 0.25;
            const tagY = nodeViewY + nodeViewHeight + padding * zoom;

            const transform = `translate(${tagX - TAG_SIZE / 2}px, ${tagY}px) scale(${zoom})`;

            return html`
              <div
                class="category-tag-wrapper"
                style=${styleMap({
                  transform,
                  transformOrigin: 'left top',
                })}
              >
                <div
                  class="category-tag"
                  style=${styleMap({
                    color: info.colors.text,
                    fontSize: `${fontSize}px`,
                  })}
                >
                  #${info.label}
                </div>
              </div>
            `;
          }
        )}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'edgeless-mindmap-node-categories': EdgelessMindmapNodeCategories;
  }
}
